import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../lib/api';
import { Breadcrumb } from '../components/Breadcrumb';
import { AiCreditsCard } from '../components/ai/AiCreditsCard';

export const Route = createFileRoute('/w/$workspaceId/plan')({
  component: PlanPage,
});

function PlanPage() {
  const { workspaceId } = Route.useParams();
  const queryClient = useQueryClient();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
  });

  const { data: planInfo, isLoading } = useQuery({
    queryKey: ['workspace-plan', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}/plan`);
      return response.data.data;
    },
  });

  // Buscar uso de storage
  const { data: storageUsage } = useQuery({
    queryKey: ['storage-usage', workspaceId],
    queryFn: async () => {
      try {
        const response = await api.get('/storage/usage');
        console.log('Storage usage response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching storage usage:', error);
        return null;
      }
    },
    enabled: !!workspaceId,
  });

  // Buscar lista de arquivos
  const { data: filesData } = useQuery({
    queryKey: ['storage-files', workspaceId],
    queryFn: async () => {
      try {
        const response = await api.get('/storage/files', {
          params: { limit: 10, offset: 0 },
        });
        console.log('Storage files response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching storage files:', error);
        return null;
      }
    },
    enabled: !!workspaceId,
  });

  // Buscar assinatura do workspace
  const { data: subscription } = useQuery({
    queryKey: ['workspace-subscription', workspaceId],
    queryFn: async () => {
      try {
        const response = await api.get(`/workspaces/${workspaceId}/subscription`);
        return response.data.data;
      } catch (error) {
        return null;
      }
    },
    enabled: !!workspaceId,
  });

  // Buscar histórico de pagamentos
  const { data: paymentHistory } = useQuery({
    queryKey: ['payment-history', workspaceId],
    queryFn: async () => {
      try {
        const response = await api.get(`/workspaces/${workspaceId}/payment-history`);
        return response.data.data;
      } catch (error) {
        return [];
      }
    },
    enabled: !!workspaceId && !planInfo?.plan?.isFree,
  });

  // Buscar histórico de assinaturas com paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: subscriptionHistory, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['subscription-history', workspaceId, currentPage],
    queryFn: async () => {
      try {
        const response = await api.get(
          `/subscriptions/workspaces/${workspaceId}/subscription-history`,
          {
            params: {
              page: currentPage,
              limit: itemsPerPage,
            },
          }
        );
        return response.data.data;
      } catch (error) {
        return { items: [], total: 0, page: 1, limit: itemsPerPage, totalPages: 0 };
      }
    },
    enabled: !!workspaceId && !planInfo?.plan?.isFree,
  });

  // Buscar próxima fatura
  const { data: upcomingInvoice } = useQuery({
    queryKey: ['upcoming-invoice', workspaceId],
    queryFn: async () => {
      try {
        const response = await api.get(`/workspaces/${workspaceId}/upcoming-invoice`);
        return response.data.data;
      } catch (error) {
        return null;
      }
    },
    enabled: !!workspaceId && !planInfo?.plan?.isFree,
  });

  // Mutation para cancelar assinatura
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/workspaces/${workspaceId}/subscription/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-subscription', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace-plan', workspaceId] });
      setShowCancelConfirm(false);
      alert('Assinatura cancelada com sucesso. Você continuará tendo acesso até o final do período atual.');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao cancelar assinatura');
    },
  });

  // Mutation para pausar assinatura
  const pauseSubscriptionMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/workspaces/${workspaceId}/subscription/pause`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-subscription', workspaceId] });
      alert('Assinatura pausada com sucesso.');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao pausar assinatura');
    },
  });

  // Mutation para retomar assinatura
  const resumeSubscriptionMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/workspaces/${workspaceId}/subscription/resume`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-subscription', workspaceId] });
      alert('Assinatura retomada com sucesso.');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao retomar assinatura');
    },
  });

  const handleWhatsApp = () => {
    window.open('https://wa.me/5591989045502', '_blank');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (amountInCents: number, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountInCents / 100);
  };

  const getSubscriptionStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: 'Ativa', color: 'bg-green-100 text-green-800' },
      past_due: { label: 'Vencida', color: 'bg-red-100 text-red-800' },
      canceled: { label: 'Cancelada', color: 'bg-gray-100 text-gray-800' },
      incomplete: { label: 'Incompleta', color: 'bg-yellow-100 text-yellow-800' },
      trialing: { label: 'Teste', color: 'bg-blue-100 text-blue-800' },
      unpaid: { label: 'Não paga', color: 'bg-red-100 text-red-800' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return config;
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusConfig = {
      paid: { label: 'Pago', color: 'bg-green-100 text-green-800' },
      open: { label: 'Aberta', color: 'bg-yellow-100 text-yellow-800' },
      void: { label: 'Cancelada', color: 'bg-gray-100 text-gray-800' },
      uncollectible: { label: 'Não cobrável', color: 'bg-red-100 text-red-800' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return config;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '12px',
              color: '#718096',
            }}
          >
            Carregando...
          </p>
        </div>
      </div>
    );
  }

  if (!planInfo?.plan) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <Breadcrumb
            items={[
              { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
              { label: 'Configurações', to: '/w/$workspaceId/settings/members', params: { workspaceId } },
              { label: 'Assinatura' },
            ]}
          />
          <div
            className="bg-white p-6 mt-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <h1
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '18px',
                fontWeight: 600,
                color: '#2D3748',
                marginBottom: '1rem',
              }}
            >
              Assinatura do Workspace
            </h1>
            <p
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#718096',
              }}
            >
              Nenhum plano atribuído a este workspace.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { plan, usage } = planInfo;

  const getPercentage = (current: number, limit: number) => {
    if (limit === 0) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getBarColor = (current: number, limit: number) => {
    const percentage = getPercentage(current, limit);
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Configurações', to: '/w/$workspaceId/settings/members', params: { workspaceId } },
            { label: 'Assinatura' },
          ]}
        />

        {/* Current Plan Card */}
        <div
          className="bg-white p-6 mt-6"
          style={{
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
          }}
        >
          <div className="mb-6">
            <h1
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '18px',
                fontWeight: 600,
                color: '#2D3748',
                marginBottom: '0.25rem',
              }}
            >
              Assinatura do Workspace
            </h1>
            <p
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#A0AEC0',
                fontWeight: 400,
              }}
            >
              Visualize os detalhes do seu plano e o uso atual dos recursos
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h2
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                {plan.name}
              </h2>
              {plan.description && (
                <p
                  className="mt-1"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                  }}
                >
                  {plan.description}
                </p>
              )}
              <p
                className="mt-2"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#4A5568',
                }}
              >
                Créditos de IA do plano:{' '}
                <strong>{(plan as { aiCreditsPerPeriod?: number }).aiCreditsPerPeriod ?? 3}</strong>{' '}
                por mês (renovação mensal; bônus adicionados pelo suporte somam ao pacote).
              </p>
            </div>
            {plan.isDefault && (
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  backgroundColor: '#DBEAFE',
                  color: '#1E40AF',
                }}
              >
                Plano Padrão
              </span>
            )}
          </div>
        </div>

        {/* Usage Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-6">
          {/* Clients Usage */}
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                Clientes
              </h3>
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                {usage.clients.current}/{usage.clients.limit}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${getBarColor(usage.clients.current, usage.clients.limit)}`}
                style={{ width: `${getPercentage(usage.clients.current, usage.clients.limit)}%` }}
              />
            </div>
            <p
              className="mt-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '11px',
                color: '#A0AEC0',
              }}
            >
              {usage.clients.limit - usage.clients.current} clientes restantes
            </p>
          </div>

          {/* Processes Usage */}
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                Processos
              </h3>
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                {usage.processes.current}/{usage.processes.limit}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${getBarColor(usage.processes.current, usage.processes.limit)}`}
                style={{ width: `${getPercentage(usage.processes.current, usage.processes.limit)}%` }}
              />
            </div>
            <p
              className="mt-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '11px',
                color: '#A0AEC0',
              }}
            >
              {usage.processes.limit - usage.processes.current} processos restantes
            </p>
          </div>

          {/* Termos Usage */}
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                Termos
              </h3>
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                {(usage.termos?.current ?? 0)}/{usage.termos?.limit ?? plan.limiteTermos ?? 0}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${getBarColor(usage.termos?.current ?? 0, usage.termos?.limit ?? plan.limiteTermos ?? 0)}`}
                style={{ width: `${getPercentage(usage.termos?.current ?? 0, usage.termos?.limit ?? plan.limiteTermos ?? 0)}%` }}
              />
            </div>
            <p
              className="mt-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '11px',
                color: '#A0AEC0',
              }}
            >
              {(usage.termos?.limit ?? plan.limiteTermos ?? 0) - (usage.termos?.current ?? 0)} termos restantes
            </p>
          </div>

          {/* Users Usage */}
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                Usuários
              </h3>
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                {usage.users.current}/{usage.users.limit}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${getBarColor(usage.users.current, usage.users.limit)}`}
                style={{ width: `${getPercentage(usage.users.current, usage.users.limit)}%` }}
              />
            </div>
            <p
              className="mt-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '11px',
                color: '#A0AEC0',
              }}
            >
              {usage.users.limit - usage.users.current} usuários restantes
            </p>
          </div>

          {/* Storage Usage */}
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                Armazenamento
              </h3>
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                {storageUsage && storageUsage.totalUsed !== undefined && storageUsage.limit !== undefined
                  ? `${((storageUsage.totalUsed || 0) / 1024 / 1024).toFixed(0)}MB/${((storageUsage.limit || 1073741824) / 1024 / 1024).toFixed(0)}MB`
                  : '0MB/1024MB'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${storageUsage && storageUsage.totalUsed !== undefined && storageUsage.limit !== undefined ? getBarColor(storageUsage.totalUsed || 0, storageUsage.limit || 1073741824) : 'bg-green-500'}`}
                style={{ 
                  width: `${storageUsage && storageUsage.totalUsed !== undefined && storageUsage.limit !== undefined ? getPercentage(storageUsage.totalUsed || 0, storageUsage.limit || 1073741824) : 0}%` 
                }}
              />
            </div>
            <p
              className="mt-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '11px',
                color: '#A0AEC0',
              }}
            >
              {storageUsage && storageUsage.totalUsed !== undefined && storageUsage.limit !== undefined
                ? `${(((storageUsage.limit || 1073741824) - (storageUsage.totalUsed || 0)) / 1024 / 1024).toFixed(0)}MB disponíveis`
                : '1024MB disponíveis'}
            </p>
          </div>

          {/* AI Credits */}
          <AiCreditsCard
            workspaceId={workspaceId}
            planCreditsPerMonth={
              (plan as { aiCreditsPerPeriod?: number }).aiCreditsPerPeriod ?? 3
            }
          />
        </div>

        {/* Files List */}
        <div
          className="bg-white p-6 mt-6"
          style={{
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
          }}
        >
          <div className="mb-4">
            <h2
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                color: '#2D3748',
                marginBottom: '0.25rem',
              }}
            >
              Arquivos Enviados
            </h2>
            <p
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#A0AEC0',
                fontWeight: 400,
              }}
            >
              Lista dos arquivos que estão ocupando espaço no workspace
            </p>
          </div>

          {!filesData || !filesData.files || filesData.files.length === 0 ? (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <p
                className="mt-2"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#718096',
                }}
              >
                Nenhum arquivo enviado ainda. Total: 0MB
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th
                      className="text-left py-3 px-4"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#718096',
                        textTransform: 'uppercase',
                      }}
                    >
                      Nome do Arquivo
                    </th>
                    <th
                      className="text-left py-3 px-4"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#718096',
                        textTransform: 'uppercase',
                      }}
                    >
                      Categoria
                    </th>
                    <th
                      className="text-left py-3 px-4"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#718096',
                        textTransform: 'uppercase',
                      }}
                    >
                      Tamanho
                    </th>
                    <th
                      className="text-left py-3 px-4"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#718096',
                        textTransform: 'uppercase',
                      }}
                    >
                      Data
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filesData.files.map((file: any) => (
                    <tr key={file.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td
                        className="py-3 px-4"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#2D3748',
                        }}
                      >
                        {file.fileName}
                      </td>
                      <td
                        className="py-3 px-4"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#718096',
                        }}
                      >
                        {file.category.replace(/_/g, ' ')}
                      </td>
                      <td
                        className="py-3 px-4"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#718096',
                        }}
                      >
                        {(file.size / 1024).toFixed(0)}KB
                      </td>
                      <td
                        className="py-3 px-4"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#718096',
                        }}
                      >
                        {new Date(file.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filesData.total > 10 && (
                <p
                  className="mt-4 text-center"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '11px',
                    color: '#A0AEC0',
                  }}
                >
                  Mostrando 10 de {filesData.total} arquivos
                </p>
              )}
            </div>
          )}
        </div>

        {/* Subscription Management - Only for paid plans */}
        {subscription && !plan.isFree && (
          <div
            className="bg-white p-6 mt-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <h2
              className="mb-4"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                color: '#2D3748',
              }}
            >
              Gerenciar Assinatura
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Status */}
              <div>
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                    marginBottom: '4px',
                  }}
                >
                  Status da Assinatura
                </p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    getSubscriptionStatusBadge(subscription.status).color
                  }`}
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {getSubscriptionStatusBadge(subscription.status).label}
                </span>
              </div>

              {/* Current Period End */}
              {subscription.currentPeriodEnd && (
                <div>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#718096',
                      marginBottom: '4px',
                    }}
                  >
                    {subscription.cancelAtPeriodEnd ? 'Cancela em' : 'Próxima renovação'}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#2D3748',
                    }}
                  >
                    {formatDate(subscription.currentPeriodEnd)}
                  </p>
                </div>
              )}
            </div>

            {/* Upcoming Invoice */}
            {upcomingInvoice && !subscription.cancelAtPeriodEnd && (
              <div
                className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                <p className="text-sm text-blue-800 mb-1">
                  <strong>Próxima cobrança:</strong>{' '}
                  {formatCurrency(upcomingInvoice.amount, upcomingInvoice.currency)}
                </p>
                <p className="text-xs text-blue-600">
                  Data: {formatDate(upcomingInvoice.periodEnd)}
                </p>
              </div>
            )}

            {/* Cancellation Notice */}
            {subscription.cancelAtPeriodEnd && (
              <div
                className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                <p className="text-sm text-yellow-800">
                  ⚠️ Sua assinatura foi cancelada e expirará em{' '}
                  {formatDate(subscription.currentPeriodEnd)}. Você pode retomar a assinatura a qualquer momento antes desta data.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {!subscription.cancelAtPeriodEnd && subscription.status === 'active' && (
                <>
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    disabled={cancelSubscriptionMutation.isPending}
                    className="px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-50"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    Cancelar Assinatura
                  </button>
                  {/* <button
                    onClick={() => pauseSubscriptionMutation.mutate()}
                    disabled={pauseSubscriptionMutation.isPending}
                    className="px-4 py-2 text-yellow-600 border border-yellow-600 rounded-lg hover:bg-yellow-50 transition-colors text-sm font-medium disabled:opacity-50"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    {pauseSubscriptionMutation.isPending ? 'Pausando...' : 'Pausar Assinatura'}
                  </button> */}
                </>
              )}

              {subscription.cancelAtPeriodEnd && (
                <button
                  onClick={() => resumeSubscriptionMutation.mutate()}
                  disabled={resumeSubscriptionMutation.isPending}
                  className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {resumeSubscriptionMutation.isPending ? 'Retomando...' : 'Retomar Assinatura'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Subscription History - Only for paid plans */}
        {subscriptionHistory && subscriptionHistory.items.length > 0 && !plan.isFree && (
          <div
            className="bg-white p-6 mt-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <h2
              className="mb-4"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                color: '#2D3748',
              }}
            >
              Histórico de Assinaturas
            </h2>

            {isLoadingHistory ? (
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#718096' }}>
                Carregando histórico...
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th
                          className="text-left py-3 px-4"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#718096',
                            textTransform: 'uppercase',
                          }}
                        >
                          Plano
                        </th>
                        <th
                          className="text-left py-3 px-4"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#718096',
                            textTransform: 'uppercase',
                          }}
                        >
                          Período
                        </th>
                        <th
                          className="text-left py-3 px-4"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#718096',
                            textTransform: 'uppercase',
                          }}
                        >
                          Ciclo
                        </th>
                        <th
                          className="text-left py-3 px-4"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#718096',
                            textTransform: 'uppercase',
                          }}
                        >
                          Valor
                        </th>
                        <th
                          className="text-left py-3 px-4"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#718096',
                            textTransform: 'uppercase',
                          }}
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptionHistory.items.map((item: any) => {
                        const billingCycleMap: Record<string, string> = {
                          monthly: 'Mensal',
                          quarterly: 'Trimestral',
                          semiannual: 'Semestral',
                          annual: 'Anual',
                        };

                        const statusMap: Record<string, { label: string; color: string }> = {
                          active: { label: 'Ativo', color: '#48BB78' },
                          past_due: { label: 'Vencido', color: '#F56565' },
                          canceled: { label: 'Cancelado', color: '#718096' },
                          trialing: { label: 'Trial', color: '#4299E1' },
                          incomplete: { label: 'Incompleto', color: '#ED8936' },
                          unpaid: { label: 'Não Pago', color: '#F56565' },
                        };

                        const status = statusMap[item.status] || { label: item.status, color: '#718096' };

                        return (
                          <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td
                              className="py-3 px-4"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#2D3748',
                              }}
                            >
                              {item.planName}
                            </td>
                            <td
                              className="py-3 px-4"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#2D3748',
                              }}
                            >
                              {new Date(item.periodStart).toLocaleDateString('pt-BR')} -{' '}
                              {new Date(item.periodEnd).toLocaleDateString('pt-BR')}
                            </td>
                            <td
                              className="py-3 px-4"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#2D3748',
                              }}
                            >
                              {item.billingCycle ? billingCycleMap[item.billingCycle] || item.billingCycle : '-'}
                            </td>
                            <td
                              className="py-3 px-4"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#2D3748',
                              }}
                            >
                              {item.amount ? `R$ ${(item.amount / 100).toFixed(2)}` : '-'}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: status.color,
                                  backgroundColor: `${status.color}15`,
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                }}
                              >
                                {status.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {subscriptionHistory.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#718096',
                      }}
                    >
                      Mostrando {subscriptionHistory.items.length} de {subscriptionHistory.total} registros
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          fontWeight: 500,
                          color: currentPage === 1 ? '#CBD5E0' : '#4A5568',
                          backgroundColor: 'white',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: '1px solid #E2E8F0',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Anterior
                      </button>
                      <span
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#2D3748',
                          padding: '6px 12px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        Página {currentPage} de {subscriptionHistory.totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.min(subscriptionHistory.totalPages, prev + 1))
                        }
                        disabled={currentPage === subscriptionHistory.totalPages}
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          fontWeight: 500,
                          color: currentPage === subscriptionHistory.totalPages ? '#CBD5E0' : '#4A5568',
                          backgroundColor: 'white',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: '1px solid #E2E8F0',
                          cursor: currentPage === subscriptionHistory.totalPages ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Payment History - Only for paid plans */}
        {paymentHistory && paymentHistory.length > 0 && !plan.isFree && (
          <div
            className="bg-white p-6 mt-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <h2
              className="mb-4"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                color: '#2D3748',
              }}
            >
              Histórico de Pagamentos
            </h2>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th
                      className="text-left py-3 px-4"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#718096',
                        textTransform: 'uppercase',
                      }}
                    >
                      Data
                    </th>
                    <th
                      className="text-left py-3 px-4"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#718096',
                        textTransform: 'uppercase',
                      }}
                    >
                      Valor
                    </th>
                    <th
                      className="text-left py-3 px-4"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#718096',
                        textTransform: 'uppercase',
                      }}
                    >
                      Status
                    </th>
                    <th
                      className="text-left py-3 px-4"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#718096',
                        textTransform: 'uppercase',
                      }}
                    >
                      Fatura
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment: any) => (
                    <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td
                        className="py-3 px-4"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#2D3748',
                        }}
                      >
                        {formatDate(payment.createdAt)}
                      </td>
                      <td
                        className="py-3 px-4"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#2D3748',
                          fontWeight: 600,
                        }}
                      >
                        {formatCurrency(payment.amount, payment.currency)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs ${
                            getPaymentStatusBadge(payment.status).color
                          }`}
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {getPaymentStatusBadge(payment.status).label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {payment.invoiceUrl && (
                          <a
                            href={payment.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-700 text-xs"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                            }}
                          >
                            Ver Fatura
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Upgrade Button */}
        <div
          className="bg-white p-6 mt-6"
          style={{
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
          }}
        >
          <div className="text-center">
            <h3
              className="mb-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                color: '#2D3748',
              }}
            >
              Precisa de mais recursos?
            </h3>
            <p
              className="mb-4"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#718096',
              }}
            >
              Entre em contato com nossa equipe para conhecer nossos planos e adquirir mais recursos para seu workspace.
            </p>
            <button
              onClick={handleWhatsApp}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-[10px] transition-colors hover:opacity-80"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                backgroundColor: '#10B981',
                color: '#FFFFFF',
                fontWeight: 500,
              }}
            >
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Falar com Vendas no WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4 text-red-600">
              Confirmar Cancelamento
            </h2>
            <p className="mb-6 text-gray-700">
              Tem certeza que deseja cancelar sua assinatura? Você continuará tendo acesso até{' '}
              {subscription?.currentPeriodEnd && formatDate(subscription.currentPeriodEnd)}.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => cancelSubscriptionMutation.mutate()}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                disabled={cancelSubscriptionMutation.isPending}
              >
                {cancelSubscriptionMutation.isPending ? 'Cancelando...' : 'Sim, Cancelar'}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
              >
                Não, Manter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

